import { useState, useCallback, useEffect } from "react";
import { Button, Typography, Flex, Tag, Row, Col, Card, Skeleton, Input, notification } from "antd";
import { ArrowLeftOutlined, EditOutlined, CloseOutlined, CheckOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";

import "./ProjectDetail.scss";
import { ProjectStages } from "projects/constants";
import { useProject, useUpdateProject } from "projects/hooks";
import { ProjectListForm, SharePointFolderStatus } from "projects/components";
import { notifyProjectWarnings } from "projects/utils/notifyProjectWarnings";
import { NotesList, AddNoteModal } from "notes/components";
import { EligibilityChecksPanel } from "eligibility/components/EligibilityChecksPanel";
import variables from "../../../_variables.scss";

export const ProjectDetail = () => {
  const { projectId, organizationId } = useParams<{
    projectId: string;
    organizationId: string;
  }>();
  const navigate = useNavigate();
  const { data: project, isLoading, error } = useProject(projectId);
  const [isEditing, setIsEditing] = useState(false);
  const [notesRefreshTrigger, setNotesRefreshTrigger] = useState(0);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const { updateProject, loading: updateLoading } = useUpdateProject();

  // Show loading until project loads
  const isPageLoading = isLoading;

  // The route changes `:projectId` without remounting this page, so edit state
  // would otherwise follow the user to the next project — landing them on a
  // dirty form they never opened for editing.
  useEffect(() => {
    setIsEditing(false);
    setIsEditingName(false);
  }, [projectId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleFormFinish = async () => {
    setIsEditing(false);
    // Trigger notes refresh after successful save
    setNotesRefreshTrigger(prev => prev + 1);
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleAddNote = useCallback(() => {
    setIsAddNoteModalOpen(true);
  }, []);

  const handleAddNoteModalClose = useCallback(() => {
    setIsAddNoteModalOpen(false);
  }, []);

  const handleAddNoteSuccess = useCallback(() => {
    setNotesRefreshTrigger(prev => prev + 1);
  }, []);

  const handleStartEditName = useCallback(() => {
    setEditedName(project?.name ?? "");
    setIsEditingName(true);
  }, [project?.name]);

  const handleCancelEditName = useCallback(() => {
    setIsEditingName(false);
    setEditedName("");
  }, []);

  const handleSaveName = useCallback(async () => {
    if (!project || !editedName.trim() || editedName.trim() === project.name) {
      handleCancelEditName();
      return;
    }

    try {
      const result = await updateProject({ ...project, name: editedName.trim() });
      notification.success({
        message: "Project name updated",
        description: "The project name has been updated successfully.",
        duration: 3,
      });
      notifyProjectWarnings(notification, result?.warnings);
      setIsEditingName(false);
    } catch {
      notification.error({
        message: "Failed to update project name",
        description: "An error occurred while updating the project name.",
      });
    }
  }, [project, editedName, updateProject, handleCancelEditName]);

  // Notes fetching is handled by NotesList's internal useNotes() hook (uncontrolled mode)

  if (error || (!project && !isLoading)) {
    return (
      <div className="project-detail-container">
        <Button
          type="link"
          className="project-detail-back-link"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
        >
          Back to Projects
        </Button>
        <Typography.Title level={3}>Project Not Found</Typography.Title>
        <Typography.Text type="secondary">
          The project you are looking for does not exist or you do not have access to it.
        </Typography.Text>
      </div>
    );
  }

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'in progress':
        return 'processing';
      case 'complete':
      case 'completed':
        return 'success';
      case 'deficient':
        return 'error';
      case 'on hold':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStageLabel = (stage?: string) => {
    if (!stage) return "";
    const match = ProjectStages.find(
      (s) =>
        typeof s.value === "string" &&
        s.value?.toLowerCase() === stage.toLowerCase()
    );
    return match?.label ?? stage.replace(/_/g, " ");
  };

  return (
    <div className="project-detail-container">
      <Button
        type="link"
        className="project-detail-back-link"
        icon={<ArrowLeftOutlined />}
        onClick={handleBack}
      >
        Back to Projects
      </Button>

      {isLoading ? (
        <div className="project-detail-header">
          <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16}>
            <div>
              <Skeleton.Input active style={{ width: 300, height: 32, marginBottom: 8 }} />
              <Flex gap={8} align="center">
                <Skeleton.Input active size="small" style={{ width: 100 }} />
                <Skeleton.Input active size="small" style={{ width: 100 }} />
                <Skeleton.Button active size="small" style={{ width: 80 }} />
              </Flex>
            </div>
            <Skeleton.Button active style={{ width: 80 }} />
          </Flex>
        </div>
      ) : project && (
        <div className="project-detail-header">
          <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16}>
            <div>
              {isEditingName ? (
                <Flex gap={8} align="center" className="project-detail-title-edit">
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onPressEnter={handleSaveName}
                    onKeyDown={(e) => e.key === "Escape" && handleCancelEditName()}
                    autoFocus
                    style={{ fontSize: 24, fontWeight: 600, maxWidth: 400 }}
                  />
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={handleSaveName}
                    loading={updateLoading}
                    size="small"
                  />
                  <Button
                    icon={<CloseOutlined />}
                    onClick={handleCancelEditName}
                    size="small"
                  />
                </Flex>
              ) : (
                <Flex gap={8} align="center">
                  <Typography.Title className="project-detail-title" level={3} style={{ marginBottom: 0 }}>
                    {project.name}
                  </Typography.Title>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={handleStartEditName}
                    className="project-detail-name-edit-btn"
                  />
                </Flex>
              )}
              <Flex gap={8} align="center" className="project-detail-meta">
                <Typography.Text type="secondary">{project.projectCode}</Typography.Text>
                <span className="project-detail-separator">|</span>
                <Typography.Text type="secondary">{getStageLabel(project.stage)}</Typography.Text>
                <Tag color={getStatusColor(project.status)}>{project.status ? project.status.charAt(0).toUpperCase() + project.status.slice(1) : null}</Tag>
                <SharePointFolderStatus project={project} />
              </Flex>
            </div>
            <Button
              type={isEditing ? "default" : "primary"}
              icon={isEditing ? <CloseOutlined /> : <EditOutlined />}
              onClick={handleEditToggle}
            >
              {isEditing ? "Cancel" : "Edit"}
            </Button>
          </Flex>
        </div>
      )}

      <Row gutter={[16, 16]} className="project-detail-content">
        <Col xs={24} lg={16}>
          <Card loading={isPageLoading} bordered={false}>
            {project && (
              <Flex data-testid='notesList' vertical gap={variables.gap}>
                <h1>Details</h1>
                {/* Keyed on the project so switching projects rebuilds the
                    form instance — its antd form store and save state must not
                    carry over to a different project's Details. */}
                <ProjectListForm key={project.id} project={project} onFinish={handleFormFinish} onCancel={handleCancel} isEditing={isEditing} />
              </Flex>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Flex vertical gap={variables.gap}>
            <Card loading={isPageLoading} bordered={false}>
              {project && (
                <NotesList
                  title="Notes"
                  showCard={false}
                  projectId={project.id}
                  allowDismiss={false}
                  showProjectName={false}
                  refreshTrigger={notesRefreshTrigger}
                  onAddNote={handleAddNote}
                />
              )}
            </Card>
            {project?.id && (
              <EligibilityChecksPanel
                organizationId={organizationId}
                projectId={project.id}
              />
            )}
          </Flex>
        </Col>
      </Row>

      {project?.id && (
        <AddNoteModal
          open={isAddNoteModalOpen}
          projectId={project.id}
          onClose={handleAddNoteModalClose}
          onSuccess={handleAddNoteSuccess}
        />
      )}
    </div>
  );
};
